// "Scan & advise" (docs/plans/2026-10-03-sandbox-hosts-ontwerp.md §16.3):
// how many heavy and light sandboxes fit on a host, from what the worker's
// scan found. One pure function, shared by the API (which computes it for
// every response) and the web (types). Browser-safe, no zod.
import type { Workload } from '../constants/index.js'
import type { SandboxHostKind, SandboxHostScan } from './index.js'

/** Sandbox ceilings per workload — the one source; worker resource-limits.ts derives WORKLOAD_RESOURCE_LIMITS from it. */
export const SANDBOX_WORKLOAD_SIZES: Record<Workload, { cpus: number; memoryMb: number }> = {
  heavy: { cpus: 1, memoryMb: 2048 },
  light: { cpus: 0.5, memoryMb: 512 },
}

/** Kept back for the system. 'local' also runs API, web, Postgres and the worker. */
export const SANDBOX_HOST_RESERVE: Record<'local' | 'remote', { cpus: number; memoryMb: number }> = {
  local: { cpus: 2, memoryMb: 3072 },
  remote: { cpus: 1, memoryMb: 1024 },
}
/** Light sandboxes may book up to 2× the available CPU in ceilings; memory is never overbooked. */
export const LIGHT_CPU_OVERCOMMIT = 2
/** Light slots ≤ max(4 × heavy, 4), and never more than 32. */
export const LIGHT_PER_HEAVY_CAP = 4
export const LIGHT_SLOTS_MAX = 32
/** Disk warnings (not limits). */
export const DISK_WARN_FREE_GIB = 20
/** agent-base + runner images + egress proxy, roughly. */
export const DISK_RUNNER_IMAGES_GIB = 8
/** Writable layer + logs; /workspace is tmpfs (memory). */
export const DISK_PER_SANDBOX_GIB = 1
export const DISK_WARN_USED_FRACTION = 0.9

/** Extra memory a run with sub-tasks may take on top of its ceiling (worker withSubtaskHeadroom). */
const SUBTASK_HEADROOM_MB = 768

const MIB = 1024 ** 2
const GIB = 1024 ** 3
const KNOWN_ARCHITECTURES = ['x86_64', 'aarch64']

export interface SandboxHostAdviceInputs {
  kind: SandboxHostKind
  cpus: number
  memoryBytes: number
  reserve: { cpus: number; memoryBytes: number }
  /** Limits of the inspected foreign containers; `unlimited` = how many have no memory limit. */
  foreign: { cpus: number; memoryBytes: number; unlimited: number }
  /** What is left for sandboxes; ≥ 0. */
  available: { cpus: number; memoryBytes: number }
  sizes: typeof SANDBOX_WORKLOAD_SIZES
  lightCap: number
  disk: { freeBytes: number; totalBytes: number; neededBytes: number } | null
}

export interface SandboxHostAdvice {
  heavySlots: number
  lightSlots: number
  /** Plain English, one sentence each, in the order the UI shows them. */
  reasons: string[]
  warnings: string[]
  inputs: SandboxHostAdviceInputs
}

/** floor() that forgives float noise such as 6.999999999 from 0.1-step CPU sums. */
function floorSafe(n: number): number {
  return Math.floor(n + 1e-9)
}

/** Rounded to 0.1, without a trailing ".0". */
function num(n: number): string {
  return String(Math.round(n * 10) / 10)
}

function gib(bytes: number): string {
  return num(bytes / GIB)
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

/**
 * Pure. null when the scan failed or lacks NCPU/MemTotal. `current` = the
 * host's configured slots, for comparison warnings. Every sandbox is counted
 * at its ceiling (§16.1 item 5); own running sandboxes are not subtracted —
 * they live inside the slots.
 */
export function adviseSandboxHostSlots(
  scan: SandboxHostScan,
  kind: SandboxHostKind,
  current?: { heavySlots: number | null; lightSlots: number | null },
): SandboxHostAdvice | null {
  const cpus = scan.docker?.cpus
  const memoryBytes = scan.docker?.memoryBytes
  if (!scan.ok || cpus == null || memoryBytes == null) return null

  const sizes = SANDBOX_WORKLOAD_SIZES
  const heavyMem = sizes.heavy.memoryMb * MIB
  const lightMem = sizes.light.memoryMb * MIB
  const reserveSize = SANDBOX_HOST_RESERVE[kind === 'local' ? 'local' : 'remote']
  const reserve = { cpus: reserveSize.cpus, memoryBytes: reserveSize.memoryMb * MIB }
  const foreign = {
    cpus: scan.foreign?.cpuLimit ?? 0,
    memoryBytes: scan.foreign?.memoryLimitBytes ?? 0,
    unlimited: scan.foreign?.unlimitedMemory ?? 0,
  }

  // 1–5 of the rule in §16.3.
  const cpuAvail = Math.max(0, cpus - reserve.cpus - foreign.cpus)
  const memAvail = Math.max(0, memoryBytes - reserve.memoryBytes - foreign.memoryBytes)
  const heavyByCpu = floorSafe(cpuAvail / sizes.heavy.cpus)
  const heavyByMem = floorSafe(memAvail / heavyMem)
  const heavy = Math.min(heavyByCpu, heavyByMem)
  const lightByMem = floorSafe((memAvail - heavy * heavyMem) / lightMem)
  const lightByCpu = Math.max(0, floorSafe((cpuAvail * LIGHT_CPU_OVERCOMMIT - heavy * sizes.heavy.cpus) / sizes.light.cpus))
  const lightCap = Math.min(Math.max(LIGHT_PER_HEAVY_CAP * heavy, LIGHT_PER_HEAVY_CAP), LIGHT_SLOTS_MAX)
  const light = Math.max(0, Math.min(lightByMem, lightByCpu, lightCap))
  const capBinds = lightCap < Math.min(lightByMem, lightByCpu)

  const neededBytes = (DISK_RUNNER_IMAGES_GIB + (heavy + light) * DISK_PER_SANDBOX_GIB) * GIB
  const disk = scan.disk ? { freeBytes: scan.disk.freeBytes, totalBytes: scan.disk.totalBytes, neededBytes } : null

  const reasons: string[] = []
  reasons.push(`This host has ${num(cpus)} CPUs and ${gib(memoryBytes)} GiB of memory.`)
  reasons.push(
    `${num(reserve.cpus)} CPU and ${gib(reserve.memoryBytes)} GiB are kept back for the system` +
      (kind === 'local' ? ' and for Goblic itself (API, web, database, worker)' : '') +
      '.',
  )
  if (scan.foreign && scan.foreign.containers > 0) {
    const n = scan.foreign.containers
    const runs = `${n} other ${plural(n, 'container runs', 'containers run')} here`
    reasons.push(
      foreign.cpus === 0 && foreign.memoryBytes === 0
        ? `${runs} without limits, so nothing is taken off for ${plural(n, 'it', 'them')}.`
        : `${runs} with limits of ${num(foreign.cpus)} CPU and ${gib(foreign.memoryBytes)} GiB in total; that is taken off.`,
    )
  }
  reasons.push(`That leaves ${num(cpuAvail)} CPU and ${gib(memAvail)} GiB for sandboxes.`)
  reasons.push(
    `A heavy sandbox may use up to ${num(sizes.heavy.cpus)} CPU and ${gib(heavyMem)} GiB, so ${heavy} fit; ` +
      `${heavyByMem <= heavyByCpu ? 'memory' : 'CPU'} is the limit.`,
  )
  const lightSize = `Light sandboxes (up to ${num(sizes.light.cpus)} CPU, ${gib(lightMem)} GiB)`
  reasons.push(
    !capBinds && lightByCpu < lightByMem
      ? `${lightSize} may share the CPUs up to ${LIGHT_CPU_OVERCOMMIT}× over, so ${light} fit; CPU is the limit.`
      : `${lightSize} get the memory heavy slots leave: ${light}${capBinds ? `, capped at ${lightCap}` : ''}.`,
  )
  if (light < LIGHT_PER_HEAVY_CAP && heavy >= 1) {
    reasons.push(`Each heavy slot you give up makes room for up to ${Math.floor(heavyMem / lightMem)} light ones.`)
  }
  if (scan.own && scan.own.heavy + scan.own.light + scan.own.unknown > 0) {
    reasons.push(`${scan.own.heavy} heavy and ${scan.own.light} light Goblic sandboxes run here now; they count inside these slots.`)
  }
  reasons.push(`Runs that use sub-tasks may need up to ${SUBTASK_HEADROOM_MB} MB more each; leave some room if you use delegation.`)

  const warnings: string[] = []
  if (scan.foreign && scan.foreign.unlimitedMemory > 0) {
    const n = scan.foreign.unlimitedMemory
    warnings.push(
      `${n} other ${plural(n, 'container has', 'containers have')} no memory limit; what they really use is not in this advice. Check docker stats on the host.`,
    )
  }
  if (scan.foreign && scan.foreign.inspected < scan.foreign.containers) {
    warnings.push(
      `Only ${scan.foreign.inspected} of ${scan.foreign.containers} other containers were looked at; the advice may be too high.`,
    )
  }
  if (!disk) {
    warnings.push(`Disk space could not be measured: ${scan.diskError ?? 'no result'}`)
  } else {
    if (disk.freeBytes < DISK_WARN_FREE_GIB * GIB) {
      warnings.push(`Only ${gib(disk.freeBytes)} GiB free in Docker's data directory; keep at least ${DISK_WARN_FREE_GIB} GiB free.`)
    }
    if (disk.freeBytes < neededBytes) {
      warnings.push(`Runner images and ${heavy + light} sandboxes need about ${gib(neededBytes)} GiB; only ${gib(disk.freeBytes)} GiB is free.`)
    }
    const usedFraction = disk.totalBytes > 0 ? (disk.totalBytes - disk.freeBytes) / disk.totalBytes : 0
    if (usedFraction > DISK_WARN_USED_FRACTION) {
      warnings.push(`Docker's disk is ${Math.round(usedFraction * 100)}% full.`)
    }
  }
  const arch = scan.docker?.architecture
  if (arch && !KNOWN_ARCHITECTURES.includes(arch)) {
    warnings.push(`Architecture ${arch} is untested; runner images may not build.`)
  }
  if (scan.docker?.operatingSystem?.includes('Docker Desktop')) {
    warnings.push("Docker Desktop: the memory above is the Docker VM's, not the machine's.")
  }
  if (heavy === 0 && light === 0) warnings.push('This host has no room for a sandbox.')
  if (current) {
    const compare = (label: string, set: number | null, advised: number) => {
      if (set === null || set > advised) warnings.push(`${label} slots are set to ${set ?? 'no limit'}; this host holds about ${advised}.`)
    }
    compare('Heavy', current.heavySlots, heavy)
    compare('Light', current.lightSlots, light)
  }

  return {
    heavySlots: heavy,
    lightSlots: light,
    reasons,
    warnings,
    inputs: {
      kind,
      cpus,
      memoryBytes,
      reserve,
      foreign,
      available: { cpus: cpuAvail, memoryBytes: memAvail },
      sizes,
      lightCap,
      disk,
    },
  }
}
